import type { TranslationFunction } from "@src/packages/i18n";
import type { CycleHeaderState } from "../hooks/resolveCycleHeaderState";
import { formatPhaseTime, formatRelativePhaseDate } from "./formatPhaseDate";
import {
  presentRetrievalPreparation,
  type RetrievalPreparationPerspective,
} from "./retrievalPreparation";

export interface HeroCopy {
  title: string;
  summary: string;
  /**
   * Small label above the title (e.g. "Stimulation · day 6") to orient the
   * patient in the cycle. Absent for states without a meaningful phase name
   * (`unknown_active`, `result_recorded`) or when the beta-wait hero owns
   * its own eyebrow ("Two-week wait").
   */
  eyebrow?: { label: string };
}

const HERO_PREFIX = "homepage.review_concept.hero_dynamic";

// Per-state phase label used in the hero eyebrow. Beta states are omitted
// because the two-week-wait hero renders its own eyebrow from wait context.
const EYEBROW_TYPE_KEY: Partial<Record<CycleHeaderState["kind"], string>> = {
  pre_treatment: "pre_treatment",
  stim_trigger_logged: "stimulation",
  stim_monitoring_next: "stimulation",
  stim_med_next: "stimulation",
  stim_generic: "stimulation",
  lining_prep: "lining_preparation",
  retrieval_scheduled: "retrieval",
  retrieval_done_pending: "retrieval",
  fertilization: "fertilization",
  insemination: "insemination",
  embryo_dev: "embryo_development",
  transfer_scheduled: "transfer",
};

/**
 * A partner's words for the states that speak to the patient about her own
 * body, or about something only she does. Every state absent here already
 * reads the same for both of them ("Retrieval Monday", "Your embryos are
 * developing"), and titles that carry a date are never replaced.
 */
const SUPPORTING_COPY_KEY: Partial<
  Record<CycleHeaderState["kind"], { title?: string; summary?: string }>
> = {
  unknown_active: {
    summary: "each_step_your_partner_logs_keeps_the_team_informed",
  },
  result_recorded: { summary: "your_partner_closes_the_cycle_when_ready" },
  pre_treatment: { title: "getting_ready_for_stimulation" },
  stim_trigger_logged: { summary: "the_eggs_are_finishing_their_maturation" },
  stim_monitoring_next: { summary: "the_next_visit_confirms_trigger_timing" },
  stim_med_next: { summary: "injections_stay_at_the_same_time_each_day" },
  stim_generic: {
    title: "the_follicles_are_growing",
    summary: "bloating_is_normal_and_the_schedule_matters",
  },
  // `retrieval_scheduled` is absent: its summary is the shared retrieval
  // preparation, which already has a partner's words (`buildBaseCopy`).
  retrieval_done_pending: { summary: "a_quiet_day_helps_your_partner_recover" },
  fertilization: { title: "the_eggs_are_in_the_lab" },
  transfer_scheduled: { summary: "what_transfer_day_asks" },
  beta_scheduled: { summary: "the_medication_continues_until_the_blood_test" },
  beta_pending: {
    summary: "the_blood_test_is_booked_once_the_clinic_confirms",
  },
};

function buildEyebrow(
  state: CycleHeaderState,
  t: TranslationFunction,
): HeroCopy["eyebrow"] {
  const typeKey = EYEBROW_TYPE_KEY[state.kind];
  if (!typeKey) return undefined;
  const type = t(`${HERO_PREFIX}.eyebrow_type.${typeKey}`);
  const dayInPhase = "dayInPhase" in state ? state.dayInPhase : null;
  const label = dayInPhase
    ? t(`${HERO_PREFIX}.eyebrow_with_day`, {
        params: { type, day: String(dayInPhase) },
      })
    : t(`${HERO_PREFIX}.eyebrow_no_day`, { params: { type } });
  return { label };
}

/**
 * State-driven hero copy for the homepage. Returns null for states that
 * the static per-variant fallback (`buildHeroStatusCopy`) handles better —
 * `no_cycle` and `cycle_closed` depend on pre/post-cycle scenarios.
 *
 * Copy is educational + body-context (what's happening physically to the
 * user), intentionally distinct from the operational Care page header
 * ("Stim · jour 4") and the Cycle Detail metadata chip.
 *
 * `viewerIsSupporting` is a partner reading the same cycle. The body this copy
 * describes is not theirs, so the states that address it get a partner's words.
 */
export function presentCycleHeaderForHero(args: {
  state: CycleHeaderState;
  t: TranslationFunction;
  locale: string | undefined;
  now?: Date;
  viewerIsSupporting?: boolean;
}): HeroCopy | null {
  const { state, t, locale } = args;
  const now = args.now ?? new Date();
  const ownCopy = buildBaseCopy(
    state,
    t,
    locale,
    now,
    args.viewerIsSupporting ? "partner" : "self",
  );
  if (!ownCopy) return null;
  const supporting = args.viewerIsSupporting
    ? SUPPORTING_COPY_KEY[state.kind]
    : undefined;
  const base = supporting
    ? {
        title: supporting.title
          ? t(`${HERO_PREFIX}.supporting.${supporting.title}`)
          : ownCopy.title,
        summary: supporting.summary
          ? t(`${HERO_PREFIX}.supporting.${supporting.summary}`)
          : ownCopy.summary,
      }
    : ownCopy;
  const eyebrow = buildEyebrow(state, t);
  return eyebrow ? { ...base, eyebrow } : base;
}

function buildBaseCopy(
  state: CycleHeaderState,
  t: TranslationFunction,
  locale: string | undefined,
  now: Date,
  perspective: RetrievalPreparationPerspective,
): Omit<HeroCopy, "eyebrow"> | null {
  switch (state.kind) {
    case "no_cycle":
    case "cycle_closed":
      return null;
    case "unknown_active":
      return {
        title: t(`${HERO_PREFIX}.unknown_active_title`),
        summary: t(`${HERO_PREFIX}.unknown_active_summary`),
      };
    // Deliberately says nothing about what the result was. The hero is shared
    // across every ending, and naming one here would celebrate a loss.
    case "result_recorded":
      return {
        title: t(`${HERO_PREFIX}.result_recorded_title`),
        summary: t(`${HERO_PREFIX}.result_recorded_summary`),
      };
    case "pre_treatment":
      return {
        title: t(`${HERO_PREFIX}.pre_treatment_title`),
        summary: t(`${HERO_PREFIX}.pre_treatment_summary`),
      };
    case "stim_trigger_logged":
      return {
        title: t(`${HERO_PREFIX}.stim_trigger_logged_title`),
        summary: t(`${HERO_PREFIX}.stim_trigger_logged_summary`),
      };
    case "stim_monitoring_next": {
      const when = formatRelativePhaseDate(state.apptDate, locale, t, { now });
      return {
        title: t(`${HERO_PREFIX}.stim_monitoring_title`, {
          params: { when },
        }),
        summary: t(`${HERO_PREFIX}.stim_monitoring_summary`),
      };
    }
    case "stim_med_next": {
      const when = formatRelativePhaseDate(state.medDate, locale, t, { now });
      const time = formatPhaseTime(state.medTime, locale);
      return {
        title: t(`${HERO_PREFIX}.stim_med_title`, {
          params: { when, time },
        }),
        summary: t(`${HERO_PREFIX}.stim_med_summary`),
      };
    }
    case "lining_prep": {
      if (state.transferDate) {
        const when = formatRelativePhaseDate(state.transferDate, locale, t, {
          now,
        });
        return {
          title: t(`${HERO_PREFIX}.transfer_scheduled_title`, {
            params: { when },
          }),
          summary: t(`${HERO_PREFIX}.keep_taking_medications_until_transfer`),
        };
      }
      if (state.nextMonitoringDate) {
        const when = formatRelativePhaseDate(
          state.nextMonitoringDate,
          locale,
          t,
          { now },
        );
        return {
          title: t(`${HERO_PREFIX}.lining_check_on`, { params: { when } }),
          summary: t(`${HERO_PREFIX}.this_scan_shows_whether_lining_is_ready`),
        };
      }
      return {
        title: t(`${HERO_PREFIX}.your_lining_is_building`),
        summary: t(
          `${HERO_PREFIX}.keep_taking_medications_lining_checked_first`,
        ),
      };
    }
    case "stim_generic":
      return {
        title: t(`${HERO_PREFIX}.stim_generic_title`),
        summary: t(`${HERO_PREFIX}.stim_generic_summary`),
      };
    case "retrieval_scheduled": {
      const when = formatRelativePhaseDate(state.retrievalDate, locale, t, {
        now,
      });
      return {
        title: t(`${HERO_PREFIX}.retrieval_scheduled_title`, {
          params: { when },
        }),
        // The same words the step card and the treatment hub give: her clinic's
        // instructions when it recorded them, otherwise its timing, not ours.
        summary: presentRetrievalPreparation({
          t,
          clinicInstructions: state.clinicInstructions,
          perspective,
        }).fasting,
      };
    }
    case "retrieval_done_pending":
      return {
        title: t(`${HERO_PREFIX}.retrieval_done_pending_title`),
        summary: t(`${HERO_PREFIX}.retrieval_done_pending_summary`),
      };
    case "fertilization":
      return {
        title: t(`${HERO_PREFIX}.fertilization_title`),
        summary: t(`${HERO_PREFIX}.fertilization_summary`),
      };
    // Worded for the patient and her partner alike: "the uterus", not "your".
    case "insemination": {
      const title = state.inseminationDate
        ? t(`${HERO_PREFIX}.insemination_on`, {
            params: {
              when: formatRelativePhaseDate(state.inseminationDate, locale, t, {
                now,
              }),
            },
          })
        : t(`${HERO_PREFIX}.insemination_is_the_next_step`);
      return {
        title,
        summary: t(`${HERO_PREFIX}.clinic_places_prepared_sperm_in_the_uterus`),
      };
    }
    case "embryo_dev":
      return {
        // The day is in the eyebrow ("Embryo development · day 3").
        title: t(`${HERO_PREFIX}.your_embryos_are_developing`),
        summary: t(`${HERO_PREFIX}.lab_watches_embryos_until_blastocyst`),
      };
    case "transfer_scheduled": {
      const when = formatRelativePhaseDate(state.transferDate, locale, t, {
        now,
      });
      return {
        title: t(`${HERO_PREFIX}.transfer_scheduled_title`, {
          params: { when },
        }),
        summary: t(`${HERO_PREFIX}.some_clinics_ask_for_a_full_bladder`),
      };
    }
    case "beta_scheduled": {
      const when = formatRelativePhaseDate(state.betaDate, locale, t, { now });
      return {
        title: t(`${HERO_PREFIX}.beta_scheduled_title`, {
          params: { when },
        }),
        summary: t(`${HERO_PREFIX}.beta_scheduled_summary`),
      };
    }
    case "beta_pending":
      return {
        title: t(`${HERO_PREFIX}.beta_pending_title`),
        summary: t(`${HERO_PREFIX}.beta_pending_summary`),
      };
  }
}
